import { useState, useEffect, ChangeEvent } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import Button from "@atlaskit/button";
import { Field } from "@atlaskit/form";
import TextField from "@atlaskit/textfield";
import { token } from "@atlaskit/tokens";

import {
    InputIcon,
    useAccountCreationContext
} from "@/components/signup/account-creation";
import { emailIsValid, fontSize } from "@/components/signup/utils";
import { useJiraAiFFL } from "@/templates/paid-landing/src/hooks/use-jira-ai-ffl-context";

export const InputSubmitButton = () => {
    const intl = useIntl();
    const {
        formError,
        hasSubmitted,
        isReadyForInvalid,
        isSubmitting,
        onClickSubmitButton,
        setIsEmailValid,
        setIsReadyForInvalid,
        setTypeAheadEmail,
        typeAheadEmail
    } = useAccountCreationContext();
    const { firePaidJiraAi, promptAttemptIndex } = useJiraAiFFL();
    const [isValid, setValid] = useState<boolean>();
    const [email, setEmail] = useState(typeAheadEmail);

    useEffect(() => {
        const isValid = emailIsValid(email);

        // wait to show invalid state if the user is not finished typing
        if (!isValid && !isReadyForInvalid) {
            setValid(undefined);
            return;
        }

        setValid(isValid);
        setIsEmailValid(isValid);
    }, [email, setIsEmailValid, isReadyForInvalid]);

    const buttonText = intl.formatMessage({
        id: "signupForm.accountCreation.signupButton",
        defaultMessage: "Sign up"
    });

    const placeholderText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.emailPlaceholder",
        defaultMessage: "you@company.com"
    });

    const errorMessageText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.invalidEmail",
        defaultMessage: "Please enter a valid email address."
    });

    const labelText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.workEmail",
        defaultMessage: "Work email"
    });

    return (
        <>
            <div css={containerCss}>
                <div css={inputCss}>
                    <Field
                        aria-required={true}
                        isRequired
                        label={<span css={labelChildCss}>{labelText}</span>}
                        name="email"
                    >
                        {({ fieldProps }) => (
                            <div
                                css={[
                                    isValid === true && inputValidCss,
                                    isValid === false && inputInvalidCss
                                ]}
                            >
                                <TextField
                                    {...fieldProps}
                                    autoComplete="email"
                                    aria-describedby="account_creation_email_help_text"
                                    elemAfterInput={
                                        <InputIcon valid={isValid} />
                                    }
                                    onBlur={() => setIsReadyForInvalid(true)}
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement>
                                    ) => {
                                        setEmail(e.target.value);
                                        setTypeAheadEmail(e.target.value);
                                        fieldProps.onChange(e);
                                    }}
                                    placeholder={placeholderText}
                                    testId="email_field"
                                    type="email"
                                    value={email}
                                    style={textFieldStyles}
                                />
                            </div>
                        )}
                    </Field>
                </div>
                <div css={buttonContainerCss}>
                    <Button
                        type="submit"
                        appearance="primary"
                        style={buttonStyles}
                        isDisabled={
                            isSubmitting || (hasSubmitted && !formError)
                        }
                        onClick={() => {
                            onClickSubmitButton();
                            firePaidJiraAi(
                                "AccountCreationSubmitClicked",
                                "clicked",
                                {
                                    source: "paidLandingPreview",
                                    method: "email",
                                    promptAttemptIndex
                                }
                            );
                        }}
                    >
                        {buttonText}
                    </Button>
                </div>
            </div>
            {isValid === false ? (
                <div
                    css={errorMessageCss}
                    id="account_creation_email_help_text"
                    role="alert"
                >
                    {errorMessageText}
                </div>
            ) : null}
        </>
    );
};

const containerCss = css({
    "display": "flex",
    "alignItems": "flex-end",
    "justifyContent": "space-between",
    "gap": "0.5rem",
    "marginBlockEnd": "0.35rem",
    "@media (max-width: 1023px)": {
        flexDirection: "column"
    }
});

const inputCss = css({
    width: "100%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div"]: {
        borderRadius: "1.313rem",
        borderWidth: token("border.width", "1px")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["input"]: {
        paddingInlineStart: "0.75rem",
        fontFamily: "inherit",
        backgroundColor: token("elevation.surface"),
        fontSize: fontSize.md
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["label"]: {
        fontFamily: "inherit",
        color: "inherit",
        fontWeight: 500,
        overflow: "visible",
        width: 0,
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- hide asterisk
        ["[aria-hidden=true]"]: {
            display: "none"
        }
    }
});

const labelChildCss = css({
    display: "inline-block",
    width: 250
});

const textFieldStyles = {
    height: "2.286rem",
    paddingInlineStart: "1.125rem",
    paddingInlineEnd: "1.125rem"
};

const buttonContainerCss = css({
    "@media (max-width: 1023px)": {
        width: "100%"
    }
});

const buttonStyles = {
    borderRadius: "2.5rem",
    paddingInlineStart: "1.5rem",
    paddingInlineEnd: "1.5rem",
    width: "100%"
};

const errorMessageCss = css({
    margin: "0 auto",
    fontSize: fontSize.xs,
    lineHeight: 1.5,
    color: token("color.icon.accent.red")
});

const inputInvalidCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> div": {
        borderColor: `${token("color.border.danger")} !important`
    }
});

const inputValidCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> div": {
        borderColor: `${token("color.border.success")} !important`
    }
});
